import { Box, Link } from "@mui/material";
import StepCard, { SetupStepList } from "../../../setup/components/StepCard";
import { CoreButton } from "@verity/components/core";
import { useContext, useEffect, useState } from "react";
import Apple from "../../../images/apple.svg?react";
import Google from "../../../images/google.svg?react";
import apiClient from "../../../helpers/api-client";
import i18n from "../../../i18n";
import { ThemeWrapper } from "@verity/components";
import styles from "./index.module.css";
import { PortalControllerContext } from "../../context";
import { initiateAuthenticationRedirect } from "../../../helpers/oauth";

function isIOS() {
	// Tests for iOS device characteristics
	const iosPlatforms = ["iPhone", "iPad", "iPod"];
	const isIOSPlatform = iosPlatforms.some((platform) => navigator.platform.includes(platform));

	// Additional check for iPad on iOS 13 and above
	const isIOS13Ipad =
		navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;

	// Checks for iOS in user agent string for broader compatibility
	const isIOSUserAgent = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

	return isIOSPlatform || isIOS13Ipad || isIOSUserAgent;
}

function launchApp(url?: string, sessionToken?: string, institutionId?: number) {
	let scheme = "veritymobilelockdown://";
	if (url) {
		// encoded like this to handle search params on sdk50
		scheme += `browse?encodedUrl=${encodeURIComponent(btoa(url))}`;
		if (sessionToken) {
			scheme += `&sessionToken=${sessionToken}`;
		}
		if (institutionId) {
			scheme += `&institutionId=${institutionId}`;
		}
	} else {
		scheme += "home";
	}
	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

	if (isSafari) {
		window.open(scheme);
	} else {
		window.location.href = scheme;
	}
}

export default function MobileWorkflow() {
	const { portalContext } = useContext(PortalControllerContext);
	const [requireAuthentication, setRequireAuthentication] = useState<boolean>();
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [launchUrl, setLaunchUrl] = useState<string>();
	const [sessionToken, setSessionToken] = useState<string>();

	useEffect(() => {
		if (portalContext.lms === "canvas") {
			setRequireAuthentication(true);
		} else {
			const currentAssessmentUrl = localStorage.getItem("currentAssessmentUrl");
			if (currentAssessmentUrl) {
				setLaunchUrl(currentAssessmentUrl);
				localStorage.removeItem("currentAssessmentUrl");
			}
			setRequireAuthentication(false);
		}
	}, []);

	useEffect(() => {
		if (isAuthenticated || !requireAuthentication) return;

		const getLaunchUrl = async () => {
			const tokenExists = await apiClient.lmsInfo.getTokenExists();
			const lmsCourseId = portalContext.lmsProvidedCourseId;
			if (tokenExists && lmsCourseId) {
				const sessionToken = await apiClient.lmsInfo.getSessionToken(lmsCourseId);
				if (sessionToken) {
					const currentAssessmentUrl = localStorage.getItem("currentAssessmentUrl");
					if (currentAssessmentUrl) {
						setLaunchUrl(currentAssessmentUrl);
						localStorage.removeItem("currentAssessmentUrl");
					}
					setSessionToken(sessionToken);
					setIsAuthenticated(true);
				}
			}
		};
		const lms = portalContext.lms;
		if (lms === "canvas") {
			const intervalId = setInterval(() => {
				getLaunchUrl().catch((e) => console.error(e));
			}, 2000);

			getLaunchUrl().catch((e) => console.error(e));
			return () => clearInterval(intervalId);
		}
	}, [isAuthenticated, requireAuthentication]);

	const downloadUrls = [
		"https://apps.apple.com/ca/app/yuja-verity/id6480011608",
		"https://play.google.com/store/apps/details?id=com.yuja.veritymobilelockdown&hl=en_CA&gl=US",
	];
	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.page }}>
			<SetupStepList>
				{requireAuthentication && (
					<StepCard
						title={
							isAuthenticated
								? i18n.t("portal.components.mobileWorkflowScreen.authentication.completeTitle")
								: i18n.t("portal.components.mobileWorkflowScreen.authentication.incompleteTitle")
						}
						complete={isAuthenticated}
						mobileStyling
					>
						<Box display="flex" flexDirection="column">
							{isAuthenticated ? (
								i18n.t("portal.components.mobileWorkflowScreen.authentication.completeDescription")
							) : (
								<Box display="flex" flexDirection="column" gap={1}>
									{i18n.t("portal.components.mobileWorkflowScreen.authentication.incompleteDescription")}
									<CoreButton
										sx={{ width: "fit-content" }}
										onClick={() =>
											portalContext.courseId &&
											void initiateAuthenticationRedirect({
												courseId: portalContext.courseId,
												callbackUrl: window.location.href,
											})
										}
									>
										{i18n.t("portal.components.mobileWorkflowScreen.authentication.buttonText")}
									</CoreButton>
								</Box>
							)}
						</Box>
					</StepCard>
				)}
				<StepCard title={i18n.t("portal.components.mobileWorkflowScreen.download.title")} mobileStyling>
					<Box display="flex" flexDirection="column" gap={1}>
						{i18n.t("portal.components.mobileWorkflowScreen.download.description")}
						<Box display={"flex"} gap={"12px"} sx={{ alignItems: "center" }} flexWrap={"wrap"}>
							{isIOS() ? (
								<Apple
									width={"115px"}
									cursor={"pointer"}
									onClick={() => {
										window.open(downloadUrls[0], "_blank");
									}}
								/>
							) : (
								<Google
									width={"128px"}
									cursor={"pointer"}
									onClick={() => {
										window.open(downloadUrls[1], "_blank");
									}}
								/>
							)}
						</Box>
					</Box>
				</StepCard>
				<StepCard title={i18n.t("portal.components.mobileWorkflowScreen.launch.title")} mobileStyling>
					<Box display="flex" flexDirection="column" gap={1}>
						{i18n.t("portal.components.mobileWorkflowScreen.launch.description")}
						<CoreButton
							sx={{ width: "fit-content" }}
							disabled={!launchUrl}
							onClick={() => {
								launchApp(launchUrl, sessionToken, portalContext.instituteId);
							}}
						>
							{i18n.t("portal.components.mobileWorkflowScreen.launch.buttonText")}
						</CoreButton>
					</Box>
				</StepCard>
				<Box display="flex" justifyContent="center">
					<Link href="https://support.yuja.com/hc/en-us" target="blank" color="#0B6F85">
						{i18n.t("portal.components.mobileWorkflowScreen.support")}
					</Link>
				</Box>
			</SetupStepList>
		</ThemeWrapper>
	);
}
